import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class PriceList extends ModelObject<PriceList> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Automatically Calculate Discounts
    */
    public allowsAutomaticDiscounts: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Use Gross Pricing
    */
    public usesGrossPricing: boolean = false;
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.allowsAutomaticDiscounts = jsonSerializedObject.allowsAutomaticDiscounts;
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.usesGrossPricing = jsonSerializedObject.usesGrossPricing;
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.allowsAutomaticDiscounts = this.allowsAutomaticDiscounts;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.usesGrossPricing = this.usesGrossPricing;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "PriceList.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
    ];
}


export class PriceListLine extends BaseModelObject<PriceListLine> {

    /**
    * Price List
    */
    public priceListId: Guid = Guid.createEmpty();
    /**
    * Stock Item (Expected to be product not material)
    */
    public stockItemId: Guid = Guid.createEmpty();
    private _currentListPrice: number | null = null;
    /**
    * Product Unit List Price
    */
    public get currentListPrice(): number | null { return this._currentListPrice }
    public set currentListPrice(value: number | null) {
      this._currentListPrice = value === undefined ? null : value;
    }
    /**
    * Product is Active on this Price List
    */
    public isAvailable: boolean = false;
    private _plannedListPrice: number | null = null;
    /**
    * Next List Price - prepared for list price
    */
    public get plannedListPrice(): number | null { return this._plannedListPrice }
    public set plannedListPrice(value: number | null) {
      this._plannedListPrice = value === undefined ? null : value;
    }
    private _previousListPrice: number | null = null;
    /**
    * Last List Price - Saved Last List Price so that Prices can be reversed
    */
    public get previousListPrice(): number | null { return this._previousListPrice }
    public set previousListPrice(value: number | null) {
      this._previousListPrice = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.currentListPrice = jsonSerializedObject.currentListPrice == null ? null : jsonSerializedObject.currentListPrice;
          this.isAvailable = jsonSerializedObject.isAvailable;
          this.plannedListPrice = jsonSerializedObject.plannedListPrice == null ? null : jsonSerializedObject.plannedListPrice;
          this.previousListPrice = jsonSerializedObject.previousListPrice == null ? null : jsonSerializedObject.previousListPrice;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.priceListId = this.priceListId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.currentListPrice = this.currentListPrice == null ? null : this.currentListPrice;
        toRet.isAvailable = this.isAvailable;
        toRet.plannedListPrice = this.plannedListPrice == null ? null : this.plannedListPrice;
        toRet.previousListPrice = this.previousListPrice == null ? null : this.previousListPrice;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "PriceListLine.PriceListId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "PriceListLine.StockItemId cannot be empty"},
    ];
}


export class NativePriceListLine extends BaseModelObject<NativePriceListLine> {

    /**
    * Price List
    */
    public priceListId: Guid = Guid.createEmpty();
    /**
    * Stock Item (Expected to be product not material)
    */
    public stockItemId: Guid = Guid.createEmpty();
    private _currentListPrice: number | null = null;
    /**
    * Product Unit List Price
    */
    public get currentListPrice(): number | null { return this._currentListPrice }
    public set currentListPrice(value: number | null) {
      this._currentListPrice = value === undefined ? null : value;
    }
    /**
    * Product is Active on this Price List
    */
    public isAvailable: boolean = false;
    private _plannedListPrice: number | null = null;
    /**
    * Next List Price - prepared for list price
    */
    public get plannedListPrice(): number | null { return this._plannedListPrice }
    public set plannedListPrice(value: number | null) {
      this._plannedListPrice = value === undefined ? null : value;
    }
    private _previousListPrice: number | null = null;
    /**
    * Last List Price - Saved Last List Price so that Prices can be reversed
    */
    public get previousListPrice(): number | null { return this._previousListPrice }
    public set previousListPrice(value: number | null) {
      this._previousListPrice = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.priceListId = Guid.parseOrEmpty(jsonSerializedObject.priceListId);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.currentListPrice = jsonSerializedObject.currentListPrice == null ? null : jsonSerializedObject.currentListPrice;
          this.isAvailable = jsonSerializedObject.isAvailable;
          this.plannedListPrice = jsonSerializedObject.plannedListPrice == null ? null : jsonSerializedObject.plannedListPrice;
          this.previousListPrice = jsonSerializedObject.previousListPrice == null ? null : jsonSerializedObject.previousListPrice;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.priceListId = this.priceListId.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.currentListPrice = this.currentListPrice == null ? null : this.currentListPrice;
        toRet.isAvailable = this.isAvailable;
        toRet.plannedListPrice = this.plannedListPrice == null ? null : this.plannedListPrice;
        toRet.previousListPrice = this.previousListPrice == null ? null : this.previousListPrice;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "priceListId", rule: (v: any) => !v.isEmpty() || "NativePriceListLine.PriceListId cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "NativePriceListLine.StockItemId cannot be empty"},
    ];
}


export class NativePriceListLineFilter extends ModelObject<NativePriceListLineFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _priceListIds: Guid[] | null = null;
    public get priceListIds(): Guid[] | null { return this._priceListIds }
    public set priceListIds(value: Guid[] | null) {
      this._priceListIds = value === undefined ? null : value;
    }
    private _itemIds: Guid[] | null = null;
    public get itemIds(): Guid[] | null { return this._itemIds }
    public set itemIds(value: Guid[] | null) {
      this._itemIds = value === undefined ? null : value;
    }
    public availableOrNot: EnumYesNoEither = EnumYesNoEither.either;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.priceListIds = jsonSerializedObject.priceListIds == null ? null : jsonSerializedObject.priceListIds.map((v: any) => Guid.parseOrEmpty(v));
          this.itemIds = jsonSerializedObject.itemIds == null ? null : jsonSerializedObject.itemIds.map((v: any) => Guid.parseOrEmpty(v));
          this.availableOrNot = EnumYesNoEither.getByValue(jsonSerializedObject.availableOrNot, this.availableOrNot);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.priceListIds = this.priceListIds == null ? null : this.priceListIds.map(v => v.toString());
        toRet.itemIds = this.itemIds == null ? null : this.itemIds.map(v => v.toString());
        toRet.availableOrNot = this.availableOrNot.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredNativePriceListLineResults extends ModelObject<FilteredNativePriceListLineResults> {

    public matchingCount: number = 0;
    public nativePriceListLines: NativePriceListLine[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.nativePriceListLines = jsonSerializedObject.nativePriceListLines.map((v: any) => new NativePriceListLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.nativePriceListLines = this.nativePriceListLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "nativePriceListLines", rule: (v: any) => !!v || "nativePriceListLines is required"},
            {field: "nativePriceListLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as NativePriceListLine[]).reduce((acc: Array<string>, curr: NativePriceListLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class PriceListService extends ServiceBase {

  public async getAllPriceLists(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PriceList[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.priceLists.map((v: any) => new PriceList(v));;
    return this.makeJsonRequest("priceList/v1/getAllPriceLists", req, false, extractor, opts);
  };

  public async getPriceList(tenantId: Guid, priceListId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PriceList> {
    const req = {
    tenantId:tenantId.toString(),
    priceListId:priceListId.toString(),
    };
    const extractor = (response:any) => new PriceList(response.priceList);;
    return this.makeJsonRequest("priceList/v1/getPriceList", req, false, extractor, opts);
  };

  public async getPriceLists(tenantId: Guid, priceListIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PriceList[]> {
    const req = {
    tenantId:tenantId.toString(),
    priceListIds:priceListIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.priceLists.map((v: any) => new PriceList(v));;
    return this.makeJsonRequest("priceList/v1/getPriceLists", req, false, extractor, opts);
  };

  public async savePriceList(tenantId: Guid, priceList: PriceList, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    priceList:priceList.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("priceList/v1/savePriceList", req, true, extractor, opts);
  };

  public async setPriceListIsHidden(tenantId: Guid, priceListId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    priceListId:priceListId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("priceList/v1/setPriceListIsHidden", req, true, extractor, opts);
  };

  public async getLinesForPriceList(tenantId: Guid, priceListId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PriceListLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    priceListId:priceListId.toString(),
    };
    const extractor = (response:any) => response.lines.map((v: any) => new PriceListLine(v));;
    return this.makeJsonRequest("priceList/v1/getLinesForPriceList", req, false, extractor, opts);
  };

  public async getAllNativePriceListLines(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<NativePriceListLine[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.lines.map((v: any) => new NativePriceListLine(v));;
    return this.makeJsonRequest("priceList/v1/getAllNativePriceListLines", req, false, extractor, opts);
  };

  public async getAllNativePriceListLinesByFilter(tenantId: Guid, filter: NativePriceListLineFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredNativePriceListLineResults> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredNativePriceListLineResults(response.results);;
    return this.makeJsonRequest("priceList/v1/getAllNativePriceListLinesByFilter", req, false, extractor, opts);
  };

  public async savePriceListLines(tenantId: Guid, lines: readonly PriceListLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    lines:lines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("priceList/v1/savePriceListLines", req, true, extractor, opts);
  };
}

export const priceListService = new PriceListService();